/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { BrowserView, isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import Switch from '@material-ui/core/Switch';
import moment from 'moment';
import { studyDatePresets, getDateEntry, modalityRackButtons } from './studyLobbyUtils';
import { FiltersRow } from './FiltersRow';
import { StyledTable } from './StyledTable';
import searchStudySeries from './searchStudySeries';
import capitalizeFirst from '../util/string';

const versionPackage = require('../../package.json');

export const StudiesTable = (props) => {
  const { t } = useTranslation();
  const tr = (string) => capitalizeFirst(t(string).toLowerCase());

  const studySearchConfig = useSelector((state) => state.manager.studySearchConfig);
  const studySearchColumns = studySearchConfig && studySearchConfig.searchColumns ? studySearchConfig.searchColumns : [];

  const { handleClick, sort, filters, seriesServers, onSort: handleSort, onFilterChange: handleFilterChange, handleSearchButtonClick } = props;

  const { studiesList: studies, totalStudies, rowsPerPage, currentPage, isRaim } = useSelector((state) => state.manager);
  const { WITH_ACCESSION_NUMBER_LOADING, FILTERS_PER_ROW, SEARCH_WITH_BUTTON } = useSelector((state) => state.config);

  const MOBILE_FILTERS_PER_ROW = 2;
  const DEFAULT_FILTERS_PER_ROW = 4;

  const filtersPerRow = isMobile
    ? MOBILE_FILTERS_PER_ROW
    : (FILTERS_PER_ROW ?? DEFAULT_FILTERS_PER_ROW);

  const today = moment();
  const { studyDateTo, studyDateFrom } = filters || {};
  const getDateEntryFromRange = (today, numOfDays, edge = 'start') => {
    if (typeof numOfDays !== 'number') {
      return;
    }

    if (edge === 'end') {
      return today;
    }
    today.subtract(numOfDays, 'days');
  };
  const defaultStartDate = getDateEntryFromRange(today, null, 'start');
  const defaultEndDate = getDateEntryFromRange(today, null, 'end');
  const initialStartDate = getDateEntry(studyDateFrom, defaultStartDate);
  const initialEndDate = getDateEntry(studyDateTo, defaultEndDate);
  const [startDate, setStartDate] = useState(moment.isMoment(initialStartDate) && initialStartDate.isValid() ? initialStartDate : defaultStartDate);
  const [endDate, setEndDate] = useState(moment.isMoment(initialEndDate) && initialEndDate.isValid() ? initialEndDate : defaultEndDate);

  const dateProps = {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  };

  const [filterBy, setFilterBy] = useState('StudyDate');
  const [rowClickMode, setRowClickMode] = useState(() => {
    if (typeof window === 'undefined') return 'open';
    return window.localStorage.getItem('studyRowClickMode') === 'expand' ? 'expand' : 'open';
  });
  const [selectedStudyUID, setSelectedStudyUID] = useState(null);
  const [selectedSeriesUIDs, setSelectedSeriesUIDs] = useState([]);
  const [seriesQuery, setSeriesQuery] = useState({ studyUID: null, isLoading: false, series: [], hasError: false });
  const seriesRequestId = useRef(0);
  const topScrollDiv = useRef(null);
  const tableDiv = useRef(null);
  const pendingSeriesScrollPosition = useRef(null);

  const rememberSeriesScrollPosition = (seriesList) => {
    const lobbyScroller = seriesList?.closest('.desktop, .mobile');
    pendingSeriesScrollPosition.current = {
      windowX: window.scrollX,
      windowY: window.scrollY,
      lobbyScroller,
      lobbyTop: lobbyScroller?.scrollTop || 0,
      lobbyLeft: lobbyScroller?.scrollLeft || 0,
      tableTop: tableDiv.current?.scrollTop || 0,
      seriesTop: seriesList?.scrollTop || 0,
    };
  };

  useLayoutEffect(() => {
    const scrollPosition = pendingSeriesScrollPosition.current;
    if (!scrollPosition) return;

    window.scrollTo(scrollPosition.windowX, scrollPosition.windowY);
    if (scrollPosition.lobbyScroller) {
      scrollPosition.lobbyScroller.scrollTop = scrollPosition.lobbyTop;
      scrollPosition.lobbyScroller.scrollLeft = scrollPosition.lobbyLeft;
    }
    if (tableDiv.current) tableDiv.current.scrollTop = scrollPosition.tableTop;
    const seriesList = tableDiv.current?.querySelector('.StudiesTable_BoxTable_Table_Study.is_selected .StudiesTable_BoxTable_Table_StudySeries');
    if (seriesList) seriesList.scrollTop = scrollPosition.seriesTop;
    pendingSeriesScrollPosition.current = null;
  }, [selectedSeriesUIDs]);

  const syncDivScrollPair = (e, refDivToSync) => {
    if (!e.target.scrollMoving && !refDivToSync.current.scrollMoving) {
      e.target.scrollMoving = true;
    }
    if (!refDivToSync.current.scrollMoving) {
      refDivToSync.current.scrollLeft = e.target.scrollLeft;
    } else if (e.target.scrollLeft === refDivToSync.current.scrollLeft) {
        refDivToSync.current.scrollMoving = false;
      }
  };

  const getViewingStudiesText = () => {
    let firstCounter = rowsPerPage * currentPage;
    let secondCounter = rowsPerPage * (currentPage + 1);

    if (totalStudies > 0) {
      secondCounter = secondCounter > totalStudies ? totalStudies : secondCounter;
      return `${firstCounter}-${secondCounter} / ${totalStudies}`;
    }
      return `${firstCounter}-${secondCounter}`;
  };

  // eslint-disable-next-line no-shadow
  const onDatesChange = ({ startDate, endDate }) => {
    handleFilterChange('studyDateFrom', startDate);
    handleFilterChange('studyDateTo', endDate);
  };

  const handleStudyRowClick = async (study) => {
    if (selectedStudyUID === study.StudyInstanceUID) {
      seriesRequestId.current += 1;
      setSelectedStudyUID(null);
      setSelectedSeriesUIDs([]);
      setSeriesQuery({ studyUID: null, isLoading: false, series: [], hasError: false });
      return;
    }

    setSelectedStudyUID(study.StudyInstanceUID);
    setSelectedSeriesUIDs([]);
    setSeriesQuery({ studyUID: study.StudyInstanceUID, isLoading: true, series: [], hasError: false });
    const requestId = seriesRequestId.current + 1;
    seriesRequestId.current = requestId;

    const results = await Promise.allSettled(
      seriesServers.map((server) => searchStudySeries(server, study.StudyInstanceUID)),
    );

    if (seriesRequestId.current !== requestId) return;

    const successfulResults = results.filter((result) => result.status === 'fulfilled');
    const fetchedSeries = successfulResults.flatMap((result) => result.value);
    const series = Array.from(new Map(fetchedSeries.map((seriesItem) => {
      const uid = seriesItem['0020000E']?.Value?.[0] || seriesItem.SeriesInstanceUID || JSON.stringify(seriesItem);
      return [uid, seriesItem];
    })).values());
    setSeriesQuery({
      studyUID: study.StudyInstanceUID,
      isLoading: false,
      series,
      hasError: successfulResults.length === 0,
    });
  };

  const RenderTitles = () => {
    const cellsDivs = [];
    studySearchColumns.forEach((columnConfig, i) => {
      const cellWidth = columnConfig.width || 'medium';
      cellsDivs.push(<div key={i} className={`StudiesTable_BoxTable_Table_Row_Cell ${cellWidth}_width`}>{tr(columnConfig.label)}</div>);
    });
    cellsDivs.push([
      <div key="nSeries" className="StudiesTable_BoxTable_Table_Row_Cell small_width series_column">{tr('series')}</div>,
      <div key="nInstances" className="StudiesTable_BoxTable_Table_Row_Cell small_width images_column">{tr('instances')}</div>
    ]);

    return (
      <div className="StudiesTable_BoxTable_Table_Row_Header">
        {cellsDivs}
      </div>
    );
  };

  const RenderCell = ({ study }) => {
    const rowCell = [];
    studySearchColumns.forEach((columnConfig, i) => {
      const cellValue = study[columnConfig.qidoQueryStringParam];
      const cellWidth = columnConfig.width || 'medium';
      rowCell.push(<div key={i} className={`StudiesTable_BoxTable_Table_Row_Cell ${cellWidth}_width`}>{cellValue}</div>);
    });

    rowCell.push([
      <div key="nSeries" className="StudiesTable_BoxTable_Table_Row_Cell small_width series_column">{study.NumberOfStudyRelatedSeries}</div>,
      <div key="nInstances" className="StudiesTable_BoxTable_Table_Row_Cell small_width images_column">{study.NumberOfStudyRelatedInstances}</div>
    ]);

    return rowCell;
  };

  const RenderCells = () => studies && studies.map((study) => {
    const isSelected = selectedStudyUID === study.StudyInstanceUID;

    return (
      <div key={study.StudyInstanceUID} className={`StudiesTable_BoxTable_Table_Study ${isSelected ? 'is_selected' : ''}`}>
        <div
          onClick={() => {
            if (rowClickMode === 'open') {
              handleClick(study.AccessionNumber, study.StudyInstanceUID);
              return;
            }
            handleStudyRowClick(study);
          }}
          className="StudiesTable_BoxTable_Table_Row"
        >
          <RenderCell study={study} />
        </div>
        <div
          className={`StudiesTable_BoxTable_Table_StudyActions ${isSelected ? 'is_expanded' : ''}`}
          aria-hidden={!isSelected}
          onClick={() => setSelectedStudyUID(null)}
        >
          <div
            className="StudiesTable_BoxTable_Table_StudySeries"
            onClick={(event) => event.stopPropagation()}
          >
            {seriesQuery.studyUID === study.StudyInstanceUID && seriesQuery.isLoading && (
              <div className="StudiesTable_BoxTable_Table_StudySeriesMessage">{t('series.loading')}</div>
            )}
            {seriesQuery.studyUID === study.StudyInstanceUID && seriesQuery.hasError && (
              <div className="StudiesTable_BoxTable_Table_StudySeriesMessage">{t('series.loadError')}</div>
            )}
            {seriesQuery.studyUID === study.StudyInstanceUID && !seriesQuery.isLoading && !seriesQuery.hasError && seriesQuery.series.length === 0 && (
              <div className="StudiesTable_BoxTable_Table_StudySeriesMessage">{t('series.none')}</div>
            )}
            {seriesQuery.studyUID === study.StudyInstanceUID && seriesQuery.series.map((seriesItem) => {
              const modality = seriesItem['00080060']?.Value?.[0] || seriesItem.Modality || '—';
              const seriesInstanceUID = seriesItem['0020000E']?.Value?.[0] || seriesItem.SeriesInstanceUID || '—';
              const seriesDate = seriesItem['00080021']?.Value?.[0]
                || seriesItem.SeriesDate
                || seriesItem['00080022']?.Value?.[0]
                || seriesItem.AcquisitionDate
                || study['00080020']?.Value?.[0]
                || study.StudyDate
                || '—';
              const description = seriesItem['0008103E']?.Value?.[0] || seriesItem.SeriesDescription || '—';
              const instanceCount = seriesItem['00201209']?.Value?.[0] || seriesItem.NumberOfSeriesRelatedInstances || '—';
              const seriesInputId = `${study.StudyInstanceUID}-${seriesInstanceUID}`.replace(/[^a-zA-Z0-9_-]/g, '_');
              const parsedSeriesDate = moment(seriesDate, ['YYYYMMDD', 'MMM DD, YYYY'], true);
              const formattedSeriesDate = parsedSeriesDate.isValid()
                ? parsedSeriesDate.format('DD MMM YYYY').replace(/\./g, '')
                : seriesDate;
              const instanceLabel = Number(instanceCount) === 1 ? t('series.instance.singular') : t('series.instance.plural');
              const labelValues = [
                { value: modality, className: 'series_modality' },
                { value: formattedSeriesDate, className: 'series_date' },
                { value: instanceCount === '—' ? instanceCount : `${instanceCount} ${instanceLabel}`, className: 'series_instance_count' },
              ];
              if (description !== '—') labelValues.push({ value: description, className: 'series_description' });
              const labelParts = labelValues.filter((part) => part.value !== '—');

              return (
                <label
                  key={seriesInstanceUID === '—' ? JSON.stringify(seriesItem) : seriesInstanceUID}
                  htmlFor={seriesInputId}
                  className="StudiesTable_BoxTable_Table_StudySeriesItem"
                >
                  <input
                    type="checkbox"
                    id={seriesInputId}
                    name="SeriesInstanceUID"
                    value={seriesInstanceUID}
                    checked={selectedSeriesUIDs.includes(seriesInstanceUID)}
                    disabled={!isSelected || seriesInstanceUID === '—'}
                    tabIndex={isSelected ? 0 : -1}
                    onMouseDown={(event) => {
                      rememberSeriesScrollPosition(event.currentTarget.closest('.StudiesTable_BoxTable_Table_StudySeries'));
                    }}
                    onChange={(event) => {
                      if (!pendingSeriesScrollPosition.current) {
                        rememberSeriesScrollPosition(event.currentTarget.closest('.StudiesTable_BoxTable_Table_StudySeries'));
                      }
                      setSelectedSeriesUIDs((currentUIDs) => (
                        event.target.checked
                          ? [...currentUIDs, seriesInstanceUID]
                          : currentUIDs.filter((uid) => uid !== seriesInstanceUID)
                      ));
                    }}
                  />
                  <span>
                    {labelParts.map((part, index) => (
                      <React.Fragment key={part.className}>
                        {index > 0 && ' - '}
                        <span className={part.className}>{part.value}</span>
                      </React.Fragment>
                    ))}
                  </span>
                </label>
              );
            })}
          </div>
          <div className="StudiesTable_BoxTable_Table_StudyActionButtons">
            <button
              type="button"
              className="StudiesTable_OpenStudyButton"
              aria-label={`${t('open.study')} ${study.StudyDescription || study.StudyInstanceUID}`}
              tabIndex={isSelected ? 0 : -1}
              onClick={(event) => {
                event.stopPropagation();
                handleClick(study.AccessionNumber, study.StudyInstanceUID);
              }}
            >
              {t('open.study')}
            </button>
            {selectedSeriesUIDs.length > 0 && (
              <button
                type="button"
                className="StudiesTable_OpenStudyButton"
                aria-label={t('open.selected.series')}
                tabIndex={isSelected ? 0 : -1}
                onClick={(event) => {
                  event.stopPropagation();
                  handleClick(study.AccessionNumber, study.StudyInstanceUID, selectedSeriesUIDs);
                }}
              >
                {t('open.selected.series')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  });

  const RenderPresets = () => {
    const presets = studyDatePresets();
    const modalitiesRack = modalityRackButtons();

    return (
      <div className="StudiesTable_Filters_Row">
        {presets.map(({ text, start, end }) => {
          const isSelected = startDate === start && endDate === end;

          return (
            <div key={text} className="StudiesTable_Filters_Row_Presets">
              <button
                key={text}
                type="button"
                className={`PresetDateRangePicker_button ${isSelected ? 'PresetDateRangePicker_button__selected' : ''}`}
                onClick={() => {
                  setStartDate(start);
                  setEndDate(end);
                  onDatesChange({
                    startDate: start,
                    endDate: end,
                    preset: true,
                  });
                }}
              >
                {text}
              </button>
            </div>
          );
        })}

        {!isMobile
          && modalitiesRack.map(({ text, modality }) => (
            <div className="StudiesTable_Filters_Row_Presets" key={`${text}button`}>
              <button
                key={text}
                type="button"
                className={`PresetDateRangePicker_button ${filters.modalities === text ? 'PresetDateRangePicker_button__selected' : ''}`}
                onClick={() => handleFilterChange('ModalitiesInStudy', modality)}
              >
                {text}
              </button>
            </div>
          ))}
      </div>
    );
  };

  return (
    <>
      <StyledTable filtersPerRow={filtersPerRow}>
        <div className={`StudiesTable ${isMobile && 'StudiesTable_mobile'}`}>
          <div className="StudiesTable_Filters">
            {/* {isMobile && (
                !isRaim && (
                  <div className="total_studies">
                    <span>Studies:</span>
                    <span>{`${rowsPerPage * currentPage}-${rowsPerPage * (currentPage + 1) > totalStudies ? totalStudies : rowsPerPage * (currentPage + 1)} / ${totalStudies}`}</span>
                  </div>
                )
              )} */}
            <div className="StudiesTable_Filters_Row">
              <FiltersRow
                filters={filters}
                filtersPerRow={filtersPerRow}
                onSort={handleSort}
                onValueChange={handleFilterChange}
                sortFieldName={sort.fieldName}
                sortDirection={sort.direction}
                displayText={filterBy}
                isMobile={isMobile}
                dateProps={dateProps}
              />
            </div>
            <div className="StudiesTable_Filters_Row StudiesTable_Filters_Row_Search">
              <div className="StudyRowClickMode">
                <Switch
                  checked={rowClickMode === 'expand'}
                  color="primary"
                  className="StudyRowClickModeSwitch"
                  inputProps={{
                    id: 'study-row-click-mode',
                    'aria-label': t('study.row.click.mode'),
                  }}
                  onChange={(event) => {
                    const mode = event.target.checked ? 'expand' : 'open';
                    setRowClickMode(mode);
                    window.localStorage.setItem('studyRowClickMode', mode);
                  }}
                />
                <label htmlFor="study-row-click-mode">
                  {t(rowClickMode === 'expand' ? 'study.row.expand.on.select' : 'study.row.open.on.select')}
                </label>
              </div>
              {SEARCH_WITH_BUTTON && (
                <div className="SearchButtonBox">
                  <button className="SearchButton" onClick={handleSearchButtonClick} type="button">
                    {t('filters.search')}
                  </button>
                </div>
              )}
            </div>
            <RenderPresets />
            <BrowserView>
              <div className="total_studies">
                { !isRaim && (
                  <span>
                    {`${t('table.viewing')} `}
                    {getViewingStudiesText()}
                  </span>
                  )}
                <p className="version">{versionPackage.version.replace('-', '.')}</p>
              </div>
            </BrowserView>
          </div>
          <div className="StudiesTable_BoxTable">
            <div ref={topScrollDiv} className="StudiesTable_BoxTable_Header" onScroll={(e) => syncDivScrollPair(e, tableDiv)}>
              <div className=" StudiesTable_BoxTable_Header_Content">
                <RenderTitles />
              </div>
            </div>
            <div ref={tableDiv} className="StudiesTable_BoxTable_Table" onScroll={(e) => syncDivScrollPair(e, topScrollDiv)}>
              <RenderCells />
            </div>
          </div>
        </div>
      </StyledTable>
    </>
  );
};

export default StudiesTable;
